import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { isMobileApp } from "../lib/platform";

const AuthContext = createContext(null);

// Explicit sign-out marker. Survives app relaunches so a signed-out user is
// never silently signed back in by leftover refresh-token cookies (M-06).
const SIGNED_OUT_FLAG = "ocsv_signed_out";
const wasSignedOut = () => { try { return localStorage.getItem(SIGNED_OUT_FLAG) === "1"; } catch { return false; } };
const clearSignedOut = () => { try { localStorage.removeItem(SIGNED_OUT_FLAG); } catch { /* noop */ } };
const markSignedOut = () => { try { localStorage.setItem(SIGNED_OUT_FLAG, "1"); } catch { /* noop */ } };

// Offline-first: last known-good user, so "no internet / server down" never
// looks like "signed out" or "trial ended". Only a real 401 clears a session.
const USER_CACHE = "ocsv_user_cache";
const cacheUser = (u) => { try { u ? localStorage.setItem(USER_CACHE, JSON.stringify(u)) : localStorage.removeItem(USER_CACHE); } catch { /* noop */ } };
const readCachedUser = () => {
    try {
        const u = JSON.parse(localStorage.getItem(USER_CACHE) || "null");
        if (!u) return null;
        // Re-derive access locally so an expired trial can't hide offline forever.
        const trialOk = u.trial_ends_at && new Date(u.trial_ends_at) > new Date();
        u.trial_active = !u.is_paid && !!trialOk;
        u.has_access = u.role === "admin" || !!u.is_paid || u.trial_active;
        u.offline = true;
        return u;
    } catch { return null; }
};
// Network failure / server outage — anything except a clear HTTP auth answer.
const isUnreachable = (e) => !e?.response || e.response.status >= 500 || e.response.status === 0;

export function AuthProvider({ children }) {
    const [user, setUserState] = useState(null);
    const [loading, setLoading] = useState(true);

    // Any successful sign-in (manual login, signup, OAuth) clears the marker.
    const setUser = useCallback((u) => {
        if (u) clearSignedOut();
        cacheUser(u);
        setUserState(u);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            clearSignedOut();
            cacheUser(data);
            setUserState(data);
            return data;
        } catch (e) {
            // Access token expired but refresh token may still be valid.
            // Try to mint a fresh access cookie, then re-fetch the user.
            if (e?.response?.status === 401) {
                try {
                    await api.post("/auth/refresh");
                    const { data } = await api.get("/auth/me");
                    clearSignedOut();
                    cacheUser(data);
                    setUserState(data);
                    return data;
                } catch (e2) {
                    if (isUnreachable(e2) && !wasSignedOut()) {
                        const cached = readCachedUser();
                        if (cached) { setUserState(cached); return cached; }
                    }
                    cacheUser(null);
                    setUserState(null);
                    return null;
                }
            }
            // Offline or server down — fall back to the last known-good session
            // instead of dumping a real user onto a login screen that also
            // needs the network ("any device, anytime").
            if (isUnreachable(e) && !wasSignedOut()) {
                const cached = readCachedUser();
                if (cached) { setUserState(cached); return cached; }
            }
            setUserState(null);
            return null;
        }
    }, []);

    useEffect(() => {
        // If returning from Emergent Google OAuth, AuthCallback will refresh user.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        // User explicitly signed out last session — stay signed out. Re-run the
        // server logout best-effort in case the previous one never reached it.
        if (wasSignedOut()) {
            api.post("/auth/logout").catch(() => { /* noop */ });
            setLoading(false);
            return;
        }
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const logout = async () => {
        markSignedOut();
        cacheUser(null);
        try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
        // Inside the Android/iOS wrapper also wipe the native cookie store so a
        // relaunch cannot resurrect the session from stale cookies.
        if (isMobileApp()) {
            try { await window.Capacitor?.Plugins?.CapacitorCookies?.clearAllCookies?.(); } catch { /* noop */ }
        }
        setUserState(null);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
